import {
  getMappingListDisplay,
  getStringListDisplay,
} from '@api/hooks/useOrderDetails'
import { Texto, Vertical } from '@gravitate-js/excalibrr'
import { theme } from 'antd'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { useState } from 'react'
import type { FieldChange, Load } from '../types'
import { ChangeRow } from './ChangeRow'

// Helper to check if a value is empty/null/undefined
const isEmpty = (value: unknown): boolean =>
  value === null || value === undefined || value === '' || value === '—'

// Helper to check if a change row should be shown
const hasValue = (before: unknown, after: unknown): boolean =>
  !isEmpty(before) || !isEmpty(after)

interface LoadCardProps {
  loadIndex: number
  beforeLoad: Load | null | undefined
  afterLoad: Load | null | undefined
  changes: FieldChange[]
  fallbackProduct?: string | null
}

export function LoadCard({
  loadIndex,
  beforeLoad,
  afterLoad,
  changes,
  fallbackProduct,
}: LoadCardProps) {
  const [expanded, setExpanded] = useState(false)
  const { token } = theme.useToken()

  // Determine display values from after (or before if load was removed)
  const displayLoad = afterLoad || beforeLoad
  const productName =
    displayLoad?.products.length === 0
      ? fallbackProduct
      : getMappingListDisplay(displayLoad?.products)
  const terminalName = getMappingListDisplay(displayLoad?.terminals)
  const supplierName = getMappingListDisplay(displayLoad?.suppliers)
  const priceType = getStringListDisplay(displayLoad?.price_types)

  // Check if load has any changes
  const hasChanges = changes.length > 0

  // Build summary line: Terminal · Supplier · Price Type
  const summaryParts = [terminalName, supplierName, priceType].filter(Boolean)
  const summaryText = summaryParts.join(' · ')

  // Filter changes to only those with meaningful values
  const visibleChanges = changes.filter((change) =>
    hasValue(change.before, change.after),
  )

  const handleToggle = () => {
    if (visibleChanges.length > 0) {
      setExpanded(!expanded)
    }
  }

  return (
    <div
      style={{
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: 8,
        overflow: 'hidden',
        backgroundColor: token.colorBgContainer,
      }}
    >
      {/* Collapsed Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '10px 12px',
          cursor: visibleChanges.length > 0 ? 'pointer' : 'default',
          backgroundColor: expanded ? token.colorFillTertiary : 'transparent',
          transition: 'background-color 0.2s',
        }}
        onClick={handleToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleToggle()
          }
        }}
        tabIndex={visibleChanges.length > 0 ? 0 : -1}
        role={visibleChanges.length > 0 ? 'button' : undefined}
        aria-expanded={visibleChanges.length > 0 ? expanded : undefined}
      >
        {/* Numbered badge */}
        <div
          style={{
            backgroundColor: hasChanges
              ? token.colorPrimary
              : token.colorTextTertiary,
            color: token.colorTextLightSolid,
            borderRadius: 6,
            minWidth: 24,
            height: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {loadIndex + 1}
        </div>

        {/* Summary content */}
        <Vertical gap={2} style={{ flex: 1, minWidth: 0 }}>
          <Texto weight='600' style={{ fontSize: 13, color: token.colorText }}>
            {productName}
          </Texto>
          {summaryText && (
            <Texto
              style={{
                fontSize: 12,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: token.colorTextSecondary,
              }}
            >
              {summaryText}
            </Texto>
          )}
        </Vertical>

        {/* Chevron */}
        {visibleChanges.length > 0 && (
          <div style={{ flexShrink: 0 }}>
            {expanded ? (
              <ChevronUp size={16} color={token.colorTextTertiary} />
            ) : (
              <ChevronDown size={16} color={token.colorTextTertiary} />
            )}
          </div>
        )}
      </div>

      {/* Expanded Content */}
      {expanded && visibleChanges.length > 0 && (
        <div
          style={{
            padding: 12,
            borderTop: `1px solid ${token.colorBorderSecondary}`,
            backgroundColor: token.colorFillTertiary,
          }}
        >
          <Vertical gap={4}>
            {visibleChanges.map((change, i) => (
              <ChangeRow
                key={i}
                label={change.field}
                beforeValue={change.before}
                afterValue={change.after}
              />
            ))}
          </Vertical>
        </div>
      )}
    </div>
  )
}
